import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  InputSignal,
  signal,
  WritableSignal
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SzIconComponent } from '../../display/sz-icon';
import { SzIconName } from '../../display/sz-icon/sz-icon-name.type';
import { SzTextInputAutocomplete, SzTextInputType } from './sz-text-input.types';

@Component({
  selector: 'sz-text-input',
  templateUrl: './sz-text-input.component.html',
  styleUrls: ['./sz-text-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SzIconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SzTextInputComponent),
      multi: true
    }
  ]
})
export class SzTextInputComponent implements ControlValueAccessor {
  readonly label: InputSignal<string | null> = input<string | null>(null);
  readonly placeholder: InputSignal<string> = input<string>('');
  readonly type: InputSignal<SzTextInputType> = input<SzTextInputType>('text');
  readonly autocomplete: InputSignal<SzTextInputAutocomplete> = input<SzTextInputAutocomplete>('off');
  readonly helperText: InputSignal<string | null> = input<string | null>(null);
  readonly errorText: InputSignal<string | null> = input<string | null>(null);
  readonly maxLength: InputSignal<number | null> = input<number | null>(null);
  readonly showCounter: InputSignal<boolean> = input<boolean>(false);
  readonly prefixIcon: InputSignal<SzIconName | null> = input<SzIconName | null>(null);
  readonly inputId: InputSignal<string | null> = input<string | null>(null);

  readonly value: WritableSignal<string> = signal<string>('');
  readonly disabledState: WritableSignal<boolean> = signal<boolean>(false);

  private onChange: (value: string) => void = (): void => {};
  private onTouched: () => void = (): void => {};

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledState.set(isDisabled);
  }

  onInput(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    this.value.set(target.value);
    this.onChange(target.value);
  }

  onBlur(): void {
    this.onTouched();
  }
}
